import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { I18nextProvider } from "react-i18next";
import i18n, { i18nReady } from "./i18n";
import { SERVER_URL } from "./api";
import { encryptData } from "./utils/encrypt";

// ============================================================
// 🎨 Site Branding — favicon + OG (แชร์) จากหลังบ้าน
// ============================================================
// แท็บเบราว์เซอร์กับตอนแชร์ลิงก์ (Facebook/Telegram/Twitter) จะใช้รูป/ข้อความ
// ที่แอดมินอัพโหลดผ่านหลังบ้าน — ไม่ต้อง build/deploy ใหม่ทุกครั้งที่เปลี่ยนโลโก้
// ★ OG meta ที่ crawler อ่านส่วนใหญ่ต้องมีตั้งแต่ HTML ดิบ — เราเพิ่ม static ไว้ใน
//   index.html แล้ว ส่วนตัวนี้เป็นตัว "อัพเดตสด" (แท็บ favicon เห็นทันทีที่เปลี่ยน)
const applySiteBranding = async () => {
  try {
    const res = await fetch(`${SERVER_URL}/api/v1/setting/branding`, {
      headers: { "x-encrypted": encryptData({ timestamp: Date.now() }) },
    });
    if (!res.ok) return;
    const b = (await res.json())?.data;
    if (!b) return;

    // favicon (แท็บเบราว์เซอร์)
    if (b.faviconUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = b.faviconUrl;
      // apple-touch-icon ด้วย (หน้าจอโฮมมือถือ iOS)
      let apple = document.querySelector("link[rel='apple-touch-icon']");
      if (!apple) {
        apple = document.createElement("link");
        apple.rel = "apple-touch-icon";
        document.head.appendChild(apple);
      }
      apple.href = b.faviconUrl;
    }

    // OG / Twitter (สำหรับ platform ที่อ่าน dynamic เช่น refresh หลังแชร์)
    const setMeta = (selector, attr, key, value) => {
      if (!value) return;
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, key);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", value);
    };
    setMeta('meta[property="og:image"]', "property", "og:image", b.ogImageUrl);
    setMeta('meta[name="twitter:image"]', "name", "twitter:image", b.ogImageUrl);
    setMeta('meta[property="og:title"]', "property", "og:title", b.shareTitle);
    setMeta('meta[property="og:description"]', "property", "og:description", b.shareDescription);
  } catch (e) {
    /* โหลด branding ไม่ได้ → ใช้ของเดิมใน index.html */
  }
};
applySiteBranding();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 3 * 60 * 1000,      // ★ data ຖືວ່າ fresh 3 ນາທີ (ບໍ່ refetch ຊ້ຳ)
      cacheTime: 10 * 60 * 1000,      // ★ ເກັບ cache ໄວ້ 10 ນາທີ
      refetchOnWindowFocus: false,     // ★ ບໍ່ refetch ເມື່ອກັບມາທີ່ tab
      retry: 1,                        // ★ retry ແຄ່ 1 ຄັ້ງ (ໄວຂຶ້ນ)
    },
  },
});
const root = ReactDOM.createRoot(document.getElementById("root"));

// ★ รอไฟล์ภาษาปัจจุบันโหลดเสร็จก่อน render (i18n แบบ lazy)
//   กันหน้าจอโชว์ translation key ดิบๆ ตอนเปิดเว็บครั้งแรก
i18nReady.finally(() => {
  root.render(
    <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <App />
      </I18nextProvider>
    </QueryClientProvider>
  </React.StrictMode>
  );
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// ✅ Register Service Worker for PWA (production only — dev ໃຊ້ unregister ເພື່ອບໍ່ cache code ເກົ່າ)
if (process.env.NODE_ENV === 'production') {
  serviceWorkerRegistration.register();
} else {
  serviceWorkerRegistration.unregister();
}
